// navigator.clipboard работает только в secure context (HTTPS / localhost).
// Прод-сайт открывается по голому IP/HTTP, поэтому всегда нужен fallback на
// document.execCommand("copy") через скрытый textarea, иначе кнопка
// «Скопировать» молча ничего не делает.
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (!text) {
    return false;
  }

  if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Падаем дальше на execCommand — например, если у вкладки нет
      // разрешения на clipboard-write.
    }
  }

  if (typeof document === "undefined") {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);

  const activeElement = document.activeElement as HTMLElement | null;
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }

  document.body.removeChild(textarea);
  activeElement?.focus?.();
  return ok;
};

// Копирование картинки в буфер (саппорты берут скрин юзера, дорисовывают
// стрелки и шлют обратно). Работает только через async Clipboard API
// (secure context, HTTPS-прод) — execCommand("copy") картинки не умеет.
// Chrome принимает в ClipboardItem только image/png, поэтому JPEG из
// Telegram перекодируем через canvas. Safari требует, чтобы write() был
// вызван синхронно внутри жеста пользователя — ему передаём Promise<Blob>
// (это допускает спецификация), Chrome же надёжнее работает с готовым Blob.
const blobToPng = async (blob: Blob): Promise<Blob> => {
  if (blob.type === "image/png") {
    return blob;
  }
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("image decode failed"));
      element.src = objectUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("canvas 2d unavailable");
    }
    context.drawImage(image, 0, 0);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error("canvas toBlob failed"));
        }
      }, "image/png");
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const fetchImageAsPng = async (url: string): Promise<Blob> => {
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) {
    throw new Error(`image fetch failed: ${response.status}`);
  }
  return blobToPng(await response.blob());
};

export const canCopyImageToClipboard = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof window !== "undefined" &&
  window.isSecureContext &&
  Boolean(navigator.clipboard) &&
  typeof navigator.clipboard.write === "function" &&
  typeof ClipboardItem !== "undefined";

export const copyImageToClipboard = async (url: string): Promise<boolean> => {
  if (!url || !canCopyImageToClipboard()) {
    return false;
  }
  const userAgent = navigator.userAgent;
  const isWebKit = /AppleWebKit/.test(userAgent) && !/Chrome|Chromium|Edg\//.test(userAgent);
  try {
    if (isWebKit) {
      await navigator.clipboard.write([
        new ClipboardItem({ "image/png": fetchImageAsPng(url) })
      ]);
      return true;
    }
    const png = await fetchImageAsPng(url);
    await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
    return true;
  } catch {
    return false;
  }
};
